import React from "react";

import apiService from "../../../services/ApiService";
import snakeToPascal from "../../../tools";
import CustomDatePicker from "../Input/reusables/CustomDatePicker";
import dayjs from "dayjs";
import { DATESYSTEM_TO_FORMAT } from "../../../pages/subpages/Settings";
import { useAppStore } from "../../../shared/stores/useAppStore";
import { TailwindDataGrid, TailwindInput, TailwindSelect } from "../../Tailwind";
import type { TailwindDataGridColumn } from "../../Tailwind/TailwindDataGrid";
import { useSaveIndicator } from "../../../hooks/useSaveIndicator";
import SaveIndicator from "../../SaveIndicator";
import { useEditorStore } from "../../../shared/stores/useEditorStore";
import { useQueryClient } from "@tanstack/react-query";
import { SECTION_DATA_KEY } from "../../../shared/hooks/queries/useSectionLoader";

function getEndOfMonth(dateString: string): string {
  if (!dateString || !/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return '';
  }
  const [year, month] = dateString.split('-').map(Number);

  if (isNaN(year) || isNaN(month)) {
      return '';
    }

  const lastDayOfMonth = new Date(year, month, 0);

  const yyyy = lastDayOfMonth.getFullYear();
  const mm = String(lastDayOfMonth.getMonth() + 1).padStart(2, '0');
  const dd = String(lastDayOfMonth.getDate()).padStart(2, '0');

  return `${yyyy}-${mm}-${dd}`;
}

export default function SensitivityTimeSerieField({ item, field, currentSensitivity }) {
    const isReadOnly = !useEditorStore(s => s.userRights.right_to_write);
    const scenarioId = useEditorStore(s => s.scenarioId) ?? 0;
    const queryClient = useQueryClient();

    const invalidateQueries = () => {
        // Only invalidate section-data cache — do NOT invalidate SCENARIO_TREE_KEY (see useUpdateFieldValue.ts)
        queryClient.invalidateQueries({ queryKey: [SECTION_DATA_KEY, scenarioId] });
    };

    const userSettings = useAppStore(s => s.userSettings);

    const rawSensitivityField = item.sensitivityModelData.fields[field.name];
    const sensitivityField = (rawSensitivityField && rawSensitivityField.value && rawSensitivityField.value.value) ? rawSensitivityField : null;
    const [focusedColumnIndex, setFocusedColumnIndex] = React.useState<number | null>(null);
    const { status: saveStatus, trigger: triggerSave, resolve: resolveSave } = useSaveIndicator();

    const [timingChoicesItems, setTimingChoicesItems] = React.useState([]);
    const [timingOptionsLoading, setTimingOptionsLoading] = React.useState(true);

    React.useEffect(() => {
        setTimingOptionsLoading(true);
        apiService.getTimingOptions(field.id).then(async (response) => {
            if (response) {
                const data = await response.json();
                setTimingChoicesItems(data);

                // Once options are loaded, resolve whether the current timing is a reference or custom
                const currentTimingId = (sensitivityField) ? sensitivityField.value.value.timing_id : field.currentValue.value.timing_id;
                const matchedItem = data.find((item: any) => item.id == currentTimingId);
                if (matchedItem) {
                    localState.timingChoice = matchedItem;
                }
            }
            setTimingOptionsLoading(false);
        });
    }, [field.id]);

    const initTimeSerieData = (rawData) => {
        if (typeof rawData == 'object' && !Array.isArray(rawData)) {
            const formattedList = [];

            if (Object.keys(rawData).length !== 0) {
                Object.entries(rawData).map(([date, value]) => {
                    formattedList.push({
                        'date': {
                            'value': date,
                            'address': null,
                        },
                        'end_of_period': {
                            'value': getEndOfMonth(date),
                            'address': null,
                        },
                        'value': {
                            'value': value,
                            'address': null,
                        }
                    })
                })
            }
            return formattedList;
        } else {
            return rawData
        }
    }

    const initialTiming = (sensitivityField) ? sensitivityField.value.value.timing : field.currentValue.value.timing;
    const initialTimingId = (sensitivityField) ? sensitivityField.value.value.timing_id : field.currentValue.value.timing_id;
    const initialUnit = (sensitivityField) ? sensitivityField.value.value.unit.value : field.currentValue.value.unit.value;
    const initialData = (sensitivityField) ? sensitivityField.value.value.data : field.currentValue.value.data;

    const getTimingById = (timingChoices, timingId) => {
        let ret = 'null';
        timingChoices.map((item: any) => {
            if (item.id == timingId) {
                ret = item;
            }
        })
        return ret;
    }

    const initialItem = getTimingById(timingChoicesItems, initialTimingId);

    const [localState, setLocalState] = React.useState({
        timingId: initialTimingId,
        timingChoice: initialItem,
        timing: initialTiming,
        unit: initialUnit,
        data: initTimeSerieData(initialData)
    })

    const [loader, setLoader] = React.useState(false);

    const localReload = () => {
        setLoader(!loader);
    }

    const checkValidInit = () => {
        if (initialItem != localState.timingChoice ||
            JSON.stringify(initTimeSerieData(initialData)) != JSON.stringify(localState.data))
            {
                localState.timingId = initialTimingId
                localState.timingChoice = initialItem
                localState.unit = initialUnit
                localState.data = initTimeSerieData(initialData)
                localReload();

            }
    }


    checkValidInit();

    localState.timing.start_date.value = (localState.timing.start_date.value) ? dayjs(localState.timing.start_date.value) : null;
    localState.timing.end_date.value = (localState.timing.end_date.value) ? dayjs(localState.timing.end_date.value) : null;
    localState.timing.periodicity.value = (localState.timing.periodicity.value) ? localState.timing.periodicity.value : 'null';

    const resetSensitivityValue = () => {
        apiService.deleteSensitivityValue(sensitivityField.id).then(
            async(response) => {
                if (response) {
                    delete item.sensitivityModelData.fields[field.name];
                    const originalChoice = getTimingById(timingChoicesItems, field.currentValue.value.timing_id);
                    localState.timingId = field.currentValue.value.timing_id,
                    localState.timingChoice = originalChoice,
                    localState.timing = field.currentValue.value.timing,
                    localState.unit = (field.currentValue.value.unit.value) ? field.currentValue.value.unit.value : "",
                    localState.data = initTimeSerieData(field.currentValue.value.data)

                    setLocalState(localState);
                    localReload();
                    invalidateQueries();
                }
            }
        )

    }

    const updateTimeSerie = (sensitivityFieldId) => {

        apiService.getSensitivityTimeSerie(sensitivityFieldId).then(async(response) => {
            if (response) {
                const newTs = await response.json();

                item.sensitivityModelData.fields[field.name].value.value.data = initTimeSerieData(newTs.data);

                localState.data = initTimeSerieData(newTs.data)
                localReload()

                invalidateQueries();
            }
        })
    }

    const updateTimingChoice = (value, sensitivityField, _sensitivityFieldJustInitialized) => {

        const apiValue = (value !== 'null') ? value.id : null;

        if (value == 'null') {
            apiService.postSensitivityTimingValue(sensitivityField.id, 'timing').then(async(response) => {
                if (response) {
                    const timing = await response.json()

                    timing.current_value.periodicity.value = (timing.current_value.periodicity.value) ? timing.current_value.periodicity.value : 'null';

                    sensitivityField.value.value.timing = timing.current_value;
                    sensitivityField.value.value.timing_id = timing.id

                    updateTimeSerie(sensitivityField.id)

                    localState.timingId = timing.id
                    localState.timingChoice = value;
                    localState.timing = formatNewTiming(timing)
                    localReload()

                    invalidateQueries();

                }
            }
            )
        }
        else {
            apiService.putSensitivityTimeSerie(sensitivityField.id, localState.unit, apiValue).then(
                async(response) => {
                    if (response) {
                            sensitivityField.value.value.timing = value;
                            sensitivityField.value.value.timing_id = value.id
                            updateTimeSerie(sensitivityField.id);

                            localState.timingChoice = value;

                            invalidateQueries();
                        }
                }
            )
        }
    }


    const handleTimingChoiceChange = (e) => {
        const newValue = e.target.value;

        if (sensitivityField) {
            updateTimingChoice(newValue, sensitivityField, false)
        } else {
            apiService.upsertSensitivityValue(item.sensitivityModelData.id, field.name, field.type, null).then(
                async(response) => {
                    if (response) {
                        const newSensitivityField = await response.json()

                        item.sensitivityModelData.fields[field.name] = newSensitivityField
                        updateTimingChoice(newValue, newSensitivityField, true);

                        invalidateQueries();
                    }
                }
            )
        }
    }

    const checkComplete = (timing) => {
        if (timing.start_date.value && timing.end_date.value) {
            if (timing.start_date.value.diff(timing.end_date.value, 'day') >= 0) {
                field.complete = false;
                field.timingError = 'Start date must be before End date.'
            }
            else if (timing.start_date.value.diff(timing.end_date.value, 'month') == 0) {
                field.complete = false;
                field.timingError = 'Difference Between Dates should be at least one month.'
            }
            else if (timing.periodicity.value !== 'null') {
                field.complete = true;
                field.timingError = null;
            }
            else {
                field.timingError = null;
                field.complete = false;
            }
        }
        else {
            field.complete = false;
            field.timingError = null;
        }
    }

    const formatNewTiming = (response) => {
        return {
            start_date: {
                value: response.start_date ? dayjs(response.start_date) : null,
                address: null,
            },
            end_date: {
                value: response.end_date ? dayjs(response.end_date) : null,
                address: null,
            },
            periodicity: {
                value: response.periodicity,
                address: null,
            },
        }
    }


    const handleChangeStartDate = (newValue, timingId, sensitivityFieldId) => {

        triggerSave();
        apiService.putModelDataTimingValue(timingId,
            newValue.format('YYYY-MM-DD'),
            (localState.timing.end_date.value) ? localState.timing.end_date.value.format('YYYY-MM-DD') : null,
            localState.timing.periodicity.value,
        ).then(async(response) => {
            if (response) {
                const newTiming = formatNewTiming(await response.json())

                localState.timing = newTiming;
                field.start_dateStore.isOpen = false;
                checkComplete(newTiming);
                updateTimeSerie(sensitivityFieldId);

                resolveSave();
                invalidateQueries();
            }
        })

    };

    const handleChangeStartDatewithSensitivity = (newValue) => {
        if (sensitivityField) {
            handleChangeStartDate(newValue, localState.timingId, sensitivityField.id);
        } else {
            createSensitivityAndValue(newValue, handleChangeStartDate)
        }
    }

    const handleChangeEndDate = (newValue, timingId, sensitivityFieldId) => {
        triggerSave();
        apiService.putModelDataTimingValue(timingId,
            (localState.timing.start_date.value) ? localState.timing.start_date.value.format('YYYY-MM-DD') : null,
            newValue.format('YYYY-MM-DD'),
            localState.timing.periodicity.value,
        ).then(async(response) => {
            if (response) {
                const newTiming = formatNewTiming(await response.json())

                localState.timing = newTiming;

                field.end_dateStore.isOpen = false;
                checkComplete(newTiming);
                updateTimeSerie(sensitivityFieldId);
                resolveSave();
                invalidateQueries();
            }
        })
    };

    const handleChangeEndDatewithSensitivity = (newValue) => {
        if (sensitivityField) {
            handleChangeEndDate(newValue, localState.timingId, sensitivityField.id);
        } else {
            createSensitivityAndValue(newValue, handleChangeEndDate)
        }
    }

    const handleChangePeriodicity = (e, timingId, sensitivityFieldId) => {

        triggerSave();
        apiService.putModelDataTimingValue(timingId,
            (localState.timing.start_date.value) ? localState.timing.start_date.value.format('YYYY-MM-DD') : null,
            (localState.timing.end_date.value) ? localState.timing.end_date.value.format('YYYY-MM-DD') : null,
            e.target.value,
        ).then(async(response) => {
            if (response) {
                const newTiming = formatNewTiming(await response.json())
                localState.timing = newTiming;
                checkComplete(newTiming);
                updateTimeSerie(sensitivityFieldId);
                resolveSave();
                invalidateQueries();
            }
        })
    }

    const handleChangePeriodicityWithSensitivity = (newValue) => {
        if (sensitivityField) {
            handleChangePeriodicity(newValue, localState.timingId, sensitivityField.id);
        } else {
            createSensitivityAndValue(newValue, handleChangePeriodicity)
        }
    }

    const createSensitivityAndValue = (newValue, handleChangeCallBack) => {
        apiService.upsertSensitivityValue(item.sensitivityModelData.id, field.name, field.type, null).then(
            async(response) => {
                if (response) {

                    const newSensitivityField = await response.json()

                    item.sensitivityModelData.fields[field.name] = newSensitivityField

                    apiService.postSensitivityTimingValue(newSensitivityField.id, 'timing').then(
                        async(response) => {
                            if (response) {
                                const timingResponse = await response.json()

                                timingResponse.current_value.periodicity.value = (timingResponse.current_value.periodicity.value) ? timingResponse.current_value.periodicity.value : 'null';

                                newSensitivityField.value.value.timing = timingResponse.current_value;
                                newSensitivityField.value.value.timing_id = timingResponse.id

                                localState.timingId = timingResponse.id;

                                handleChangeCallBack(newValue, timingResponse.id, newSensitivityField.id);

                                invalidateQueries();
                            }
                        }
                    )

                }
            }
        )
    }

    let mutex = false;

    const handleChangeUnit = (e) => {
        const newValue = e.target.value;

        if (item.sensitivityModelData.fields[field.name] != undefined || mutex == true) {
            apiService.putSensitivityTimeSerie(item.sensitivityModelData.fields[field.name].id, newValue, field.currentValue.value.timing_id ).then(async(_response) => {

                item.sensitivityModelData.fields[field.name].value.value.unit.value = newValue;
                localState.unit = newValue;
                invalidateQueries();
            })
        } else {
            mutex = true;
            apiService.upsertSensitivityValue(item.sensitivityModelData.id, field.name, field.type, null).then(
                async(response) => {
                    if (response) {
                        const newSensitivityField = await response.json()

                        item.sensitivityModelData.fields[field.name] = newSensitivityField

                        apiService.putSensitivityTimeSerie(newSensitivityField.id, newValue, field.currentValue.value.timing_id ).then(async(_response) => {

                            newSensitivityField.value.value.unit.value = newValue;
                            localState.unit = newValue;
                            mutex = false;
                            localReload();
                            invalidateQueries();
                        })
                    }
                }
            )
        }
    }

    const getElembyDate = (localstate, date) => {
        const [elem] = localstate.data.filter(e => e.date.value == date)

        return elem
    }

    // ---- Cell editing aligned with scenario mode ----

    const validateTimeSerieElemChange = (newValue, date) => {
        triggerSave();
        const parsedValue = newValue !== "" ? newValue.replace(',', '.') : null;

        if (sensitivityField) {
            const elem = getElembyDate(localState, date);
            apiService.putSensitivityTimeSerieElem(sensitivityField.id, elem.date.value, parsedValue).then(async(response) => {
                if (response) {
                    elem.value.value = parsedValue !== null ? parseFloat(parsedValue) : null;
                    elem.color = null;
                } else {
                    elem.color = "red";
                }
                sensitivityField.value.value.data = localState.data;
                resolveSave();
                localReload();
                invalidateQueries();
            });
        } else {
            apiService.upsertSensitivityValue(item.sensitivityModelData.id, field.name, field.type, null).then(
                async(response) => {
                    if (response) {
                        const newSensitivityField = await response.json();
                        item.sensitivityModelData.fields[field.name] = newSensitivityField;
                        localState.data = initTimeSerieData(newSensitivityField.value.value.data);

                        const newElem = getElembyDate(localState, date);

                        apiService.putSensitivityTimeSerieElem(newSensitivityField.id, date, parsedValue).then(async(_response) => {
                            if (newElem) {
                                newElem.value.value = parsedValue !== null ? parseFloat(parsedValue) : null;
                                newElem.color = null;
                            }
                            item.sensitivityModelData.fields[field.name].value.value.data = localState.data;
                            resolveSave();
                            localReload();
                            invalidateQueries();
                        });
                    }
                }
            );
        }
    };

    const handleMultiCellPaste = (text: string, startColumnIndex: number) => {
        const timeSerieDataLocal = formatTimeSerieObj(localState);
        const dataKeys = Object.keys(timeSerieDataLocal).filter(k => k !== 'name');

        const lines = text.split(/\r?\n/).filter(l => l.trim() !== '');
        const values = (lines[0] || '').split('\t').map(v => v.trim());

        const elems: { date: string; value: number }[] = [];
        for (let i = 0; i < values.length; i++) {
            const colIdx = startColumnIndex + i;
            if (colIdx >= dataKeys.length) break;

            const dateKey = dataKeys[colIdx].split('|')[0];
            let parsed = parseFloat(values[i].replace(',', '.'));
            if (isNaN(parsed)) continue;

            if (field.is_percentage) parsed = parsed / 100;

            elems.push({ date: dateKey, value: parsed });
        }

        if (elems.length > 0) {
            triggerSave();
            if (sensitivityField) {
                apiService.putSensitivitiesTimeSerieElems(sensitivityField.id, elems).then((resp) => {
                    if (resp) {
                        updateTimeSerie(sensitivityField.id);
                    }
                });
            } else {
                apiService.upsertSensitivityValue(item.sensitivityModelData.id, field.name, field.type, null).then(
                    async(response) => {
                        if (response) {
                            const newSensitivityField = await response.json();
                            item.sensitivityModelData.fields[field.name] = newSensitivityField;
                            localState.data = initTimeSerieData(newSensitivityField.value.value.data);

                            apiService.putSensitivitiesTimeSerieElems(newSensitivityField.id, elems).then(async(resp) => {
                                if (resp) {
                                    updateTimeSerie(newSensitivityField.id);
                                }
                            });
                        }
                    }
                );
            }
        }
    };

    const formatTimeSerieObj = (localstate) => {
        const ret = {
            name: 'Value',
        }

        localstate.data.map(e => {
            const rawValue = e.value.value;
            const cellValue = rawValue != null ? (field.is_percentage ? rawValue * 100 : rawValue) : null;
            ret[e.date.value + '|' + e.end_of_period.value] = cellValue;
        })
        return ret
    }

    const EditableNumberCell = ({ cellValue, fieldKey, columnIndex }: { cellValue: any; fieldKey: string; columnIndex: number }) => {
        const displayValue = cellValue != null
            ? cellValue.toLocaleString(userSettings.numberFormattingLocale, { maximumFractionDigits: 5 })
            : '';
        const [localValue, setLocalValue] = React.useState(displayValue);
        const [editing, setEditing] = React.useState(false);

        // Refs for unmount save
        const localValueRef = React.useRef(localValue);
        const editingRef = React.useRef(false);
        localValueRef.current = localValue;
        editingRef.current = editing;

        const bopDate = fieldKey.split('|')[0];

        // Save unsaved changes on unmount
        React.useEffect(() => {
            return () => {
                if (editingRef.current) {
                    const currentLocalValue = localValueRef.current;
                    const rawStr = cellValue != null ? String(cellValue) : '';
                    if (currentLocalValue !== rawStr && currentLocalValue !== displayValue) {
                        let apiValue = currentLocalValue;
                        if (field.is_percentage && apiValue !== '') {
                            const num = parseFloat(apiValue.replace(',', '.'));
                            if (!isNaN(num)) {
                                apiValue = String(num / 100);
                            }
                        }
                        validateTimeSerieElemChange(apiValue, bopDate);
                    }
                }
            };
        }, []);

        const handleBlur = () => {
            setEditing(false);
            const rawStr = cellValue != null ? String(cellValue) : '';
            if (localValue !== rawStr && localValue !== displayValue) {
                let apiValue = localValue;
                if (field.is_percentage && apiValue !== '') {
                    const num = parseFloat(apiValue.replace(',', '.'));
                    if (!isNaN(num)) {
                        apiValue = String(num / 100);
                    }
                }
                validateTimeSerieElemChange(apiValue, bopDate);
            }
        };

        const handleInputPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
            const text = e.clipboardData.getData('text/plain');
            if (text.includes('\t')) {
                e.preventDefault();
                setEditing(false);
                handleMultiCellPaste(text, columnIndex);
            }
        };

        if (isReadOnly) {
            return <span>{cellValue != null ? displayValue : <span style={{ color: '#999' }}>{field.placeholder}</span>}</span>;
        }

        return editing ? (
            <input
                type="text"
                autoFocus
                value={localValue}
                onChange={(e) => setLocalValue(e.target.value)}
                onBlur={handleBlur}
                onPaste={handleInputPaste}
                onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                className="w-full bg-transparent border-b border-slate-300 outline-none text-sm py-0.5 focus:border-primary text-center"
            />
        ) : (
            <span
                className="cursor-text w-full block text-center"
                onClick={() => {
                    setFocusedColumnIndex(columnIndex);
                    setLocalValue(cellValue != null ? String(cellValue) : '');
                    setEditing(true);
                }}
            >
                {cellValue != null ? displayValue : <span style={{ color: '#999' }}>{field.placeholder}</span>}
            </span>
        );
    };

    const renderNameCell = (params) => {
        return (
            <span className="font-bold">{snakeToPascal(params.row[params.field])}</span>
        )
    }

    const renderDateHeader = (value) => {
        const [bop, eop] = value.split('|');
        return (
            <div className="flex flex-col">
                <span className="font-bold">
                    {dayjs(bop).format(DATESYSTEM_TO_FORMAT[userSettings.dateSystem])}
                </span>
                <span className="font-bold">
                    {dayjs(eop).format(DATESYSTEM_TO_FORMAT[userSettings.dateSystem])}
                </span>
            </div>
        )
    }

    const renderNameHeader = () => {
        return (
            <div className="flex flex-col">
                <strong title='Beginning of Period'>
                    Start
                </strong>
                <strong title='End of Period'>
                    End
                </strong>
            </div>
        )
    }

    const renderNumberCell = (params, columnIndex: number) => {
        return <EditableNumberCell cellValue={params.row[params.field]} fieldKey={params.field} columnIndex={columnIndex} />;
    }

    const timeSerieData = formatTimeSerieObj(localState);

    const columns: TailwindDataGridColumn[] = [
        {
          field: 'name',
          headerName: '',
          width: 60,
          renderCell: (params) => renderNameCell(params),
          renderHeader: () => renderNameHeader(),
          sortable: false,
        },
        ...Object.keys(timeSerieData)
            .filter(key => key !== 'name')
            .map((key, index) => ({
                field: key,
                headerName: key,
                width: 120,
                renderCell: (params) => renderNumberCell(params, index),
                renderHeader: () => renderDateHeader(key),
                sortable: false,
            }))
    ];

    return (
        <div>
            <div className="flex flex-row">
                <div>
                    {!isReadOnly && (
                        <button
                            onClick={resetSensitivityValue}
                            disabled={!sensitivityField}
                            className="mr-2 h-10 w-10 flex items-center justify-center text-slate-600 hover:text-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            <span className="material-symbols-outlined text-[30px]">restart_alt</span>
                        </button>
                    )}
                </div>
                <div>
                    <p className="mb-1 text-sm font-medium text-slate-700">
                        Unit :
                    </p>
                    <TailwindInput
                        value={localState.unit}
                        placeholder='Unit'
                        readOnly={isReadOnly}
                        onChange={handleChangeUnit}
                        className="w-[200px] mb-1"
                    />
                <div className="flex flex-row mt-2">
                    <div>
                        <p className="mb-1 text-sm font-medium text-slate-700">
                            Timing :
                        </p>
                        <TailwindSelect
                            value={localState.timingChoice}
                            onChange={(value) => handleTimingChoiceChange({ target: { value } } as any)}
                            disabled={isReadOnly}
                            options={[
                                { value: 'null', label: 'Custom', isItalic: true },
                                ...timingChoicesItems.map((e: any) => ({
                                    value: e,
                                    // The scenario-level global timing is a virtual reference (id 'global').
                                    label: e.id === 'global'
                                        ? 'Global Timing'
                                        : `${e.parent_name} ${snakeToPascal(e.field_name)}`
                                }))
                            ]}
                            className="w-[200px]"
                        />
                        </div>
                        {/* Start/End date and periodicity are always shown once
                            options are loaded. When a timing reference is selected
                            (`timingChoice !== 'null'`) they come from the referenced
                            timing option and are displayed read-only; only the custom
                            case is editable. */}
                        {
                                (!timingOptionsLoading) ? (() => {
                                    const isCustomTiming = localState.timingChoice === 'null';
                                    const refTiming: any = isCustomTiming ? null : localState.timingChoice;
                                    const startCurrentValue = isCustomTiming
                                        ? ((localState.timing.start_date && localState.timing.start_date.value) ? localState.timing.start_date.value : null)
                                        : (refTiming?.start_date ? dayjs(refTiming.start_date) : null);
                                    const endCurrentValue = isCustomTiming
                                        ? ((localState.timing.end_date && localState.timing.end_date.value) ? localState.timing.end_date.value : null)
                                        : (refTiming?.end_date ? dayjs(refTiming.end_date) : null);
                                    const refPeriodicity = refTiming?.periodicity;
                                    return (
                                    <div className="ml-4">
                                    <div className="mb-3.5">
                                        <p className="text-sm font-medium text-slate-700">
                                            Start Date
                                        </p>
                                        <CustomDatePicker
                                            item={field.start_dateStore}
                                            currentValue={startCurrentValue}
                                            handleChange={handleChangeStartDatewithSensitivity}
                                            readOnly={!isCustomTiming}
                                        />
                                    </div>
                                    <div className="mb-3.5">
                                        <p className="text-sm font-medium text-slate-700">
                                            End Date
                                        </p>
                                        <CustomDatePicker
                                            item={field.end_dateStore}
                                            currentValue={endCurrentValue}
                                            handleChange={handleChangeEndDatewithSensitivity}
                                            readOnly={!isCustomTiming}
                                        />
                                    </div>
                                    {isCustomTiming && field.timingError && (
                                        <p className="text-red-600 text-sm">
                                            {field.timingError}
                                        </p>
                                    )}
                                    <p className="text-sm font-medium text-slate-700">
                                        Periodicity
                                    </p>
                                    {
                                        isCustomTiming ?
                                            <TailwindSelect
                                                value={localState.timing.periodicity.value}
                                                onChange={(value) => handleChangePeriodicityWithSensitivity({ target: { value } } as any)}
                                                disabled={isReadOnly}
                                                options={[
                                                    { value: 'null', label: 'Periodicity', isItalic: true },
                                                    ...field.periodicity_choices.map(e => ({
                                                        value: e,
                                                        label: snakeToPascal(e)
                                                    }))
                                                ]}
                                                className="w-[200px]"
                                            /> :
                                            <div
                                                className="w-[200px] flex items-center justify-between bg-slate-100 border border-dashed border-slate-300 text-slate-500 text-sm rounded-lg px-4 py-2.5 font-medium cursor-not-allowed select-none"
                                                title="Inherited from the referenced timing"
                                            >
                                                <span>{refPeriodicity && refPeriodicity !== 'null' ? snakeToPascal(refPeriodicity) : '—'}</span>
                                                <span className="material-symbols-outlined text-[16px] text-slate-400">lock</span>
                                            </div>
                                    }
                                </div>
                                    );
                                })() : null
                            }
                    </div>
                </div>
            </div>
            <SaveIndicator status={saveStatus} className="mt-2" />
            {localState.data.length > 0 && (
                <div
                    className="mt-5 mr-2"
                    tabIndex={0}
                    onPaste={(e: React.ClipboardEvent) => {
                        const text = e.clipboardData.getData('text/plain');
                        if (text.includes('\t') && focusedColumnIndex !== null) {
                            e.preventDefault();
                            handleMultiCellPaste(text, focusedColumnIndex);
                        }
                    }}
                >
                    <TailwindDataGrid
                        rows={[timeSerieData]}
                        columns={columns}
                        density="comfortable"
                    />
                </div>
            )}
        </div>
    )
}
